import { Injectable } from "@angular/core";
import { BehaviorSubject, forkJoin, Observable, of } from "rxjs";
import { catchError, map, switchMap } from "rxjs/operators";

import { ApicallOption, ApicallService } from "@core-services/apicall.service";
import { ExceptionsService } from "@core-services/exceptions.service";

import { deserializeOfferGql, FiatWallet, getOfferAmount, Offer, serializeOfferGql } from "@models";
import { TradableWallet } from "@models/interfaces/tradable_wallet";

import {
	AcceptOfferGQLService,
	BuyBciGQLService,
	BuyBciMutation,
	CreateOfferGQLService,
	SellAllBciGQLService,
	SellAllBciMutation,
	SellBciGQLService,
	SellBciMutation,
	Tax
} from "@gql/offer";
import { TRADE_TYPE } from "@models/trade-offer.model";
import { IndexWallet } from "@models/asset/index/index_wallet";
import { ITaxWithheld } from "@modules/taxes/domain/entities/taxes.model";

export interface AcceptedOfferWallets {
	assetWallet: TradableWallet;
	fiatWallet: FiatWallet;
	tradeId: string;
	isFirstTrade?: boolean;
}

type BciTradeResponse = BuyBciMutation["buyBci"] | SellBciMutation["sellBci"] | SellAllBciMutation["sellAllBci"];

@Injectable({
	providedIn: "root"
})
export class TradeOfferService {
	public isFirstTrade$: BehaviorSubject<boolean> = new BehaviorSubject<boolean>(false);
	public taxWithheld$: BehaviorSubject<Partial<ITaxWithheld>> = new BehaviorSubject<Partial<ITaxWithheld>>(null);
	public constructor(
		private apicallService: ApicallService,
		private requestOfferGQLService: CreateOfferGQLService,
		private acceptOfferGQLService: AcceptOfferGQLService,
		private buyBciGQLService: BuyBciGQLService,
		private sellBciGQLService: SellBciGQLService,
		private sellAllBciGQLService: SellAllBciGQLService,
		private exceptionsService: ExceptionsService
	) {}

	public callTradeOffer(offerData: Offer): Observable<Offer[]> {
		offerData["amount"] = getOfferAmount(offerData);
		return this.apicallService.post("trade/offer", new ApicallOption(Offer, offerData));
	}

	public createOfferGQL(offerData: Offer): Observable<Offer> {
		return this.requestOfferGQLService.mutate({ input: serializeOfferGql(offerData) }).pipe(
			map(({ data }) => deserializeOfferGql(data, offerData)),
			catchError(this.exceptionsService.extractGqlErrorAndRethrow)
		);
	}

	/**
	 * Confirms (accepts) the offer to the API.
	 *
	 * @param offerId - an id that was returned from the API when the offer was created - sent in the subscription and in the mutation
	 * @param assetWalletId - an id of the asset wallet for which we are accepting the offer
	 * @param fiatWalletId - an id of the fiat waller for which we are accepting the offer
	 * @param tradableWallet - typeof tradable wallet (e.g. CryptoWallet) - needed for the REST call to update wallets
	 * @returns - an Observable containing a hashmap with latest assetWallet and fiatWallet (created/updated during the trade execution) in case of a success
	 * or an Observable of null if the trade is pending
	 */
	public acceptOffer(
		offerId: string,
		assetWalletId: string | undefined,
		tradableWallet: typeof TradableWallet
	): Observable<AcceptedOfferWallets | null> {
		return this.acceptOfferGQLService.mutate({ input: { offerId, assetWalletId: assetWalletId } }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			switchMap(({ data: { acceptOffer } }) => {
				if (acceptOffer.__typename === "AcceptOfferInProgressResponse") {
					return of(null);
				}
				this.isFirstTrade$.next(acceptOffer.trade.isFirstTrade);

				const { fiatWalletId, assetWalletId, tradeId } =
					this.getTradeIdAndWalletIdsFromAcceptedOffer(acceptOffer);

				//handle taxWithheld only if trade is successful and taxWithheld is not null
				if (acceptOffer.trade?.taxWithheld) {
					const transactionId = acceptOffer.trade.fiatWalletTransaction?.id || null;
					const fiatWalletId = acceptOffer.trade.fiatWallet?.id || null;
					const taxWithheld = this.taxWithHeldAdapter(
						acceptOffer.trade.taxWithheld,
						transactionId,
						fiatWalletId
					);
					this.taxWithheld$.next(taxWithheld);
				} else {
					this.taxWithheld$.next(null);
				}

				return this.fetchWalletsOnOfferAccepted(assetWalletId, fiatWalletId, tradableWallet, tradeId);
			})
		);
	}

	private taxWithHeldAdapter(tax: Tax, transactionId = null, fiatWalletId = null): Partial<ITaxWithheld> {
		if (!tax) {
			return null;
		}

		return {
			isTaxServiceAvailable: tax?.isTaxServiceAvailable,
			fiatId: tax?.fiatId,
			assetId: tax?.assetId,
			taxAmountFiat: tax?.taxAmountFiat?.value,
			fiatAmountAfterTax: tax?.fiatAmountAfterTax?.value,
			taxAmountAsset: tax?.taxAmountAsset?.value,
			transactionId,
			fiatWalletId,
			isSwapBCI: false
		};
	}

	public tradeBci(
		assetId: string,
		fiatId: string,
		fiatWalletId: string,
		amount: string,
		tradeType: TRADE_TYPE,
		sellAll: boolean
	): Observable<AcceptedOfferWallets | null> {
		if (tradeType === TRADE_TYPE.SELL) {
			return sellAll
				? this.sellAllBci(assetId, fiatId, fiatWalletId)
				: this.sellBci(assetId, fiatId, fiatWalletId, amount);
		}

		return this.buyBci(assetId, fiatId, fiatWalletId, amount);
	}

	public resetOfferData(offerResponse: Offer, offerData: Offer): Offer {
		offerResponse.setUserWants(offerData.getSelectedCurrency());
		offerResponse.setAmountDefinition(offerData.getAmountDefinition());
		return offerResponse;
	}

	private buyBci(
		assetId: string,
		fiatId: string,
		fiatWalletId: string,
		amount: string
	): Observable<AcceptedOfferWallets | null> {
		return this.buyBciGQLService.mutate({ input: { assetId, fiatId, amount: { value: amount } } }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			map(({ data: { buyBci } }) => buyBci),
			switchMap((bciTradeResponse) => this.handleBciTradeResponse(bciTradeResponse, fiatWalletId))
		);
	}

	private sellBci(
		assetId: string,
		fiatId: string,
		fiatWalletId: string,
		amount: string
	): Observable<AcceptedOfferWallets | null> {
		return this.sellBciGQLService.mutate({ input: { assetId, fiatId, amount: { value: amount } } }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			map(({ data: { sellBci } }) => sellBci),
			switchMap((bciTradeResponse) => this.handleBciTradeResponse(bciTradeResponse, fiatWalletId))
		);
	}

	private sellAllBci(assetId: string, fiatId: string, fiatWalletId: string): Observable<AcceptedOfferWallets | null> {
		return this.sellAllBciGQLService.mutate({ input: { assetId, fiatId } }).pipe(
			catchError(this.exceptionsService.extractGqlErrorAndRethrow),
			map(({ data: { sellAllBci } }) => sellAllBci),
			switchMap((bciTradeResponse) => this.handleBciTradeResponse(bciTradeResponse, fiatWalletId))
		);
	}

	/**
	 * Extract relevant wallet ids to be used further down the chain to fetch newly created wallets
	 *
	 * @param trade - a hashmap returned from the API that represents a successful trade
	 * @private
	 * @returns - a hashmap containing assetWalletId, fiatWalletId and tradeId
	 */
	private getTradeIdAndWalletIdsFromAcceptedOffer({ trade }): {
		assetWalletId: string;
		fiatWalletId: string;
		tradeId: string;
	} {
		const { fiatWallet, assetWallet, tradeId } = trade;
		return { assetWalletId: assetWallet?.id, fiatWalletId: fiatWallet?.id, tradeId };
	}

	/**
	 * Maps the response from the accept offer mutation to REST API calls to get the updated wallets, so we can update the
	 * local cache in the component
	 *
	 * @param assetWalletId - an asset wallet id that was returned from the API when the offer was created
	 * @param fiatWalletId - a fiat wallet id that was returned from the API when the offer was created
	 * @param tradableWallet - typeof tradable wallet (e.g. CryptoWallet) - needed for the REST call to update wallets
	 * @param tradeId - offerId returned from the API
	 * @param taxWithheld - taxWithheld returned from the API or null
	 * @private
	 * @returns - an Observable containing a hashmap with latest assetWallet and fiatWallet (created/updated during the trade execution)
	 */
	private fetchWalletsOnOfferAccepted(
		assetWalletId: string,
		fiatWalletId: string,
		tradableWallet: typeof TradableWallet,
		tradeId: string
	): Observable<AcceptedOfferWallets> {
		return forkJoin([
			this.apicallService.get(`wallet/${assetWalletId}`, new ApicallOption(tradableWallet)),
			this.apicallService.get(`fiatwallet/${fiatWalletId}`, new ApicallOption(FiatWallet))
		]).pipe(
			map(([assetWallet, fiatWallet]) => ({
				assetWallet: assetWallet?.[0],
				fiatWallet: fiatWallet?.[0],
				tradeId: tradeId
			}))
		);
	}

	private handleBciTradeResponse(
		bciTradeResponse: BciTradeResponse,
		fiatWalletId: string
	): Observable<AcceptedOfferWallets | null> {
		//handle taxWithheld only if trade is successful
		if (bciTradeResponse.__typename === "BciTradeInProgressResponse") {
			return of(null);
		}

		const sellBciResponse = bciTradeResponse as SellBciMutation["sellBci"];

		if (sellBciResponse.__typename === "BciTradeSuccessResponse") {
			const transactionId = sellBciResponse.trade?.tradeId || null;
			const taxWithheld = this.taxWithHeldAdapter(sellBciResponse.trade.taxWithheld, transactionId, fiatWalletId);
			this.taxWithheld$.next(taxWithheld);
		}

		const {
			trade: {
				tradeId,
				wallet: { id: assetWalletId }
			}
		} = bciTradeResponse;
		return this.fetchWalletsOnBciTrade(assetWalletId, fiatWalletId, tradeId);
	}

	/**
	 * Maps the response from the BCI buy/sell to REST API calls to get the updated wallets, so we can update the
	 * local cache in the component
	 *
	 * @param assetWalletId - an asset wallet id that was returned from the API when buy/sell succeeded
	 * @param fiatWalletId - a fiat wallet id
	 * @param tradeId - received from the API on success
	 * @private
	 * @returns - an Observable containing a hashmap with latest assetWallet and fiatWallet (created/updated during the trade execution)
	 */
	private fetchWalletsOnBciTrade(
		assetWalletId: string,
		fiatWalletId: string,
		tradeId: string
	): Observable<AcceptedOfferWallets> {
		return forkJoin([
			this.apicallService.get(`wallet/${assetWalletId}`, new ApicallOption(IndexWallet)),
			this.apicallService.get(`fiatwallet/${fiatWalletId}`, new ApicallOption(FiatWallet))
		]).pipe(
			map(([assetWallet, fiatWallet]) => ({
				assetWallet: assetWallet?.[0],
				fiatWallet: fiatWallet?.[0],
				tradeId
			}))
		);
	}
}
